import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Lot, LotSource, LotTypeEnum } from '../../../../graphql/service/graphql-auto-main-service';
import { first, map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { MutationHandlingServiceV2, QueryHandlingService } from '@akebono/core';
import {
  RequestPhotoFromAuctionGQL,
  ServiceRequestAuctionLotPriceGQL,
} from '../../../../graphql/service/graphql-shared-familiar-service';
import { Observable, of, Subject } from 'rxjs';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { LotShareService } from '../../../../../services/lot-share.service';

@Component({
  selector: 'app-lot-watch-request-buy-modal',
  templateUrl: './lot-watch-request-buy-modal.component.html',
  styleUrls: ['./lot-watch-request-buy-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotWatchRequestBuyModalComponent implements OnInit, OnDestroy {
  @Input() lot: Lot;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() inspectionId: number;
  @Input() lotShared: EventEmitter<void>;

  price$: Observable<number>;

  loading$ = of(false);
  creating$ = of(false);
  lotLink$: Observable<string>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly qhs: QueryHandlingService,
    private readonly mhs: MutationHandlingServiceV2,
    private readonly modal: NzModalRef,
    private readonly requestPhotoFromAuctionGQL: RequestPhotoFromAuctionGQL,
    private readonly serviceRequestAuctionLotPriceGQL: ServiceRequestAuctionLotPriceGQL,
    private readonly lotShareService: LotShareService,
  ) {}

  ngOnInit(): void {
    const shareLotResult = this.lotShareService.share(this.lot, this.lotType, this.lotSource, {
      useFullDomain: false,
    });
    this.lotLink$ = shareLotResult.link$.pipe(shareReplay(1));
    this.fetchRequestPrice();
  }

  fetchRequestPrice(): void {
    const priceRef = this.qhs.fetch(
      this.serviceRequestAuctionLotPriceGQL,
      {
        type: this.inspectionId,
        lotId: this.lot.id,
        lotSource: this.lotSource,
        lotSubgroup: this.lotType.toString(),
      },
      'network-only',
    );
    this.loading$ = priceRef.loading;
    this.price$ = priceRef.data.pipe(
      map((res) => res?.serviceRequestAuctionLotPrice ?? 0),
      startWith(0),
    );
  }

  async buyLotWatchRequest(): Promise<void> {
    const lotLink = await this.lotLink$.toPromise();
    const mutationRef = this.mhs.mutate(
      this.requestPhotoFromAuctionGQL,
      {
        input: {
          lotLink,
          lotId: this.lot.id,
          lotSource: this.lotSource,
          lotSubgroup: this.lotType.toString(),
          type: this.inspectionId,
          lotData: `${this.lot.code} - ${this.lot.auctionName}`,
          dueDate: this.lot.auctionDatetime,
        },
      },
      {
        refetch: true,
      },
    );

    this.creating$ = mutationRef.loading;
    mutationRef.data.pipe(first(), takeUntil(this.destroy$)).subscribe(() => this.close());
  }

  close(): void {
    this.modal.destroy();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
